import axios from "axios";

// ✅ ENV based API URL (works for local + production)
// CRA:   REACT_APP_API_URL
// Vite:  VITE_API_URL
const API_BASE =
  process.env.REACT_APP_API_URL ||
  (import.meta && import.meta.env && import.meta.env.VITE_API_URL) ||
  "https://api.rangpursadhima.cloud/api"; // fallback for local dev

const API = axios.create({
  baseURL: API_BASE,
});

// Add auth token to all requests
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper to get current user info for activity logging
const getActorInfo = () => {
  try {
    const userStr = localStorage.getItem("user");
    const role = localStorage.getItem("role");

    if (userStr && role) {
      const user = JSON.parse(userStr);
      return {
        actorId: user.id || 0,
        actorRole: role || "UNKNOWN",
        actorEmail: user.email || null,
        actorMobile: user.mobile || null,
      };
    }
  } catch (e) {
    console.error("Error getting actor info:", e);
  }

  // Return defaults if nothing found
  return {
    actorId: 0,
    actorRole: localStorage.getItem("role") || "UNKNOWN",
    actorEmail: null,
    actorMobile: null,
  };
};

// ===============================
// User endpoints
// ===============================
export const getUsers = () => API.get("/users");
export const getUser = (id) => API.get(`/users/${id}`);

export const createUser = (data) => {
  const actorInfo = getActorInfo();
  return API.post("/users", { ...data, ...actorInfo });
};

export const updateUser = (id, data, isSelfEdit = false) => {
  const actorInfo = getActorInfo();
  return API.put(`/users/${id}`, { ...data, ...actorInfo, isSelfEdit });
};

export const deleteUser = (id) => {
  const actorInfo = getActorInfo();
  return API.delete(`/users/${id}`, { data: actorInfo });
};

// ===============================
// Attendance endpoints
// ===============================
export const markAttendance = (user_id, slot) => {
  const actorInfo = getActorInfo();
  return API.post("/users/attendance", { user_id, slot, ...actorInfo });
};

// Get attendance summary (for button state - returns lastMorningMarked, lastEveningMarked)
export const getAttendance = (user_id) => API.get(`/attendance/${user_id}`);

// Get attendance records with IDs (for list/delete)
export const getAttendanceRecords = (user_id) =>
  API.get(`/users/attendance/${user_id}`);

export const deleteAttendance = (attendanceId) => {
  const actorInfo = getActorInfo();
  return API.delete(`/users/attendance/${attendanceId}`, { data: actorInfo });
};

export const getAttendanceLog = (user_id) =>
  API.get(`/attendance/${user_id}/log`);

// ===============================
// States
// ===============================
export const getStates = () => API.get("/users/states");
export const getUsersByState = (state) =>
  API.get(`/users/state?state=${state}`);

// ===============================
// Image upload
// ===============================
export const uploadImage = (formData) =>
  API.post("/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// ===============================
// Admin endpoints
// ===============================
export const getAdmins = () => API.get("/admin");
export const createAdmin = (data) => API.post("/admin", data);
export const updateAdmin = (id, data) => API.put(`/admin/${id}`, data);
export const deleteAdmin = (id) => API.delete(`/admin/${id}`);
export const toggleAdminStatus = (id) =>
  API.put(`/admin/${id}/toggle-status`);

// ===============================
// Activity Logs endpoints
// ===============================
export const getLogs = (params) => API.get("/logs", { params });
export const getLogCategories = () => API.get("/logs/categories");
export const getLogActions = () => API.get("/logs/actions");
export const getLogsByCategory = (category) =>
  API.get(`/logs/category/${category}`);
export const getLogsBySevak = (sevakCode) =>
  API.get(`/logs/sevak/${sevakCode}`);
export const getLogsByUserType = (userType) =>
  API.get(`/logs/by-type/${userType}`);
export const getLogsByUserId = (userId) =>
  API.get(`/logs/user/${userId}`);
export const getLogDetail = (id) => API.get(`/logs/detail/${id}`);
export const getLogStats = () => API.get("/logs/stats");
export const getSevakSummary = (sevakCode) =>
  API.get(`/logs/sevak-summary/${sevakCode}`);

export default API;